import { useCallback, useEffect, useState } from 'react';
import { api } from '../../api/client';
import { toast } from 'sonner';
import StatusPill from '../../components/StatusPill';
import { useAuth } from '../../context/AuthContext';

type Lead = {
  id: string; company_name: string; city: string | null; monthly_spend: string | null;
  line_count: number | null; status: string; stage: string; assigned_to: string | null;
};

const COLS: { status: string; label: string }[] = [
  { status: 'novo', label: 'Novos' },
  { status: 'em_contato', label: 'Em contato' },
  { status: 'proposta_enviada', label: 'Proposta enviada' },
  { status: 'em_negociacao', label: 'Em negociação' },
  { status: 'fechado_ganho', label: 'Ganho' },
  { status: 'fechado_perdido', label: 'Perdido' },
];

export default function Pipeline() {
  const { user } = useAuth();
  const [leads, setLeads] = useState<Lead[]>([]);
  const load = useCallback(async () => {
    const d = await api.get<{ leads: Lead[] }>('/leads?sort=spend');
    setLeads(d.leads.filter((l) => !['descartado'].includes(l.status)));
  }, []);
  useEffect(() => { load(); }, [load]);

  async function mover(id: string, status: string) {
    try {
      await api.patch(`/leads/${id}`, { status });
      toast.success('Movido.');
      await load();
    } catch (err) {
      toast.error((err as { message?: string }).message ?? 'Falha ao mover');
    }
  }

  return (
    <div>
      <header>
        <p className="text-xs font-semibold tracking-widest text-[var(--text-dim)] uppercase">
          {user?.role === 'vendedor' ? 'Meu pipeline de vendas' : 'Pipeline de vendas'}
        </p>
        <h1 className="mt-1 text-xl font-semibold tracking-tight">Acompanhamento ({leads.length})</h1>
      </header>
      <div className="mt-4 grid gap-3 lg:grid-cols-6">
        {COLS.map((c) => {
          const cards = leads.filter((l) => l.status === c.status);
          return (
            <section key={c.status} className="rounded-lg bg-white border border-[var(--border)] p-2">
              <header className="flex items-center justify-between px-1 pb-2">
                <StatusPill status={c.status} />
                <span className="text-xs font-semibold tabular-nums text-[var(--text-dim)]">{cards.length}</span>
              </header>
              {cards.map((l) => (
                <div key={l.id} className="mb-2 rounded-md border border-[var(--border-strong)] p-2 text-xs hover:border-[var(--accent)]">
                  <span className="block font-medium">{l.company_name}</span>
                  <span className="block text-[var(--text-dim)]">
                    {[l.city, l.line_count ? `${l.line_count} linhas` : null, l.monthly_spend ? fmtMoney(l.monthly_spend) : null].filter(Boolean).join(' · ') || '—'}
                  </span>
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {COLS.filter((x) => x.status !== l.status && x.status !== 'fechado_perdido').slice(0, 3).map((x) => (
                      <button key={x.status} onClick={() => mover(l.id, x.status)} className="rounded border border-[var(--border-strong)] px-1.5 py-0.5 text-[10px] hover:bg-[var(--bg-subtle)]">→ {x.label}</button>
                    ))}
                  </div>
                </div>
              ))}
              {cards.length === 0 && <p className="px-1 py-3 text-center text-[11px] text-[var(--text-dim)]">—</p>}
            </section>
          );
        })}
      </div>
    </div>
  );
}

function fmtMoney(v: string | number): string {
  return Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}
